import { RestClient } from '@/api/rest-client'
import { CancelToken } from 'axios'
import { Company } from '@/api/model/company'
import { CustomerPortalBranding } from '@/api/model/customer-portal-branding'
import { ReferralNode } from '@/api/model/referral-node'
import { ActiveCampaignCredentials } from '@/api/model/credentials'
import { MailchimpCredentials } from '@/api/model/credentials'
import { TypeformCredentials } from '@/api/model/credentials'
import { User } from '@/api/model/user'
import { AutoCloseSettings } from '@/api/model/auto-close-settings'
import { OrderSettings } from '@/api/model/order-settings'
import { cloneDeep } from 'lodash'
import { PipelineConnection } from '@/api/model/pipeline-connection'
import { ref } from '@vue/composition-api'
import { Branch } from '@/api/model/branch'

/** How long (ms) a full company of another office comes from the cache: a link it switched off shows within this time */
const RELATED_COMPANY_LIFETIME = 5 * 60 * 1000
/** How long (ms) a partner card (only id, name, branding) comes from the cache: a link accepted since shows within this time */
const PARTNER_CARD_LIFETIME = 60 * 1000

export class CompanyClient extends RestClient<Company> {
    collectionName = 'companies'
    parseData = Company.fromData
    currentCompany: Company | null = null
    reactiveCurrentCompany = ref<Company>(null)
    /** Requests on their way, shared by everyone who asks for that company meanwhile */
    protected requests = new Map<string, Promise<Company>>()
    /** When a company was fetched (ms) */
    protected fetchedAt = new Map<string, number>()

    constructor() {
        super()

        if (this.decodedAccessToken) {
            this.loadCurrentCompany(false)
        }
    }

    async update(id: string, data: Company): Promise<Company> {
        const request = cloneDeep<any>(data)

        delete request.branches
        delete request.incomingReferrals
        delete request.outgoingReferrals
        delete request.credentials
        delete request.users
        delete request.shares

        if (data.customerPortalBranding instanceof CustomerPortalBranding) {
            request.customerPortalBranding = data.customerPortalBranding.resourceIdentifier
        }

        return super.update(id, request)
    }

    /**
     * keyvue-api shows another company's branches and users only while both sides accepted a link, and else only its
     * partner card (id, name, branding). So the signed-in user's own company comes from the cache until it is reloaded,
     * another office's full company for RELATED_COMPANY_LIFETIME and a partner card for PARTNER_CARD_LIFETIME. Who asks
     * for a company while it is being fetched shares that request. When such a fetch fails, the copy in the cache (if
     * any) is the answer, as it was before it expired. `fresh` always makes a new request and gives undefined when it
     * fails, so that its caller can tell.
     */
    async get(id: string, fresh: boolean = false): Promise<Company> {
        const cached = this.cache.get(id)
        if (!fresh && cached && this.isUpToDate(id, cached)) {
            return cached
        }

        // A request on its way may be a fresh one, which gives undefined when it fails: this caller gets the cached copy
        if (!fresh && this.requests.has(id)) {
            return this.requests.get(id).then(company => company || this.cache.get(id))
        }

        const generation = this.cacheGeneration
        const request: Promise<Company> = super.get(id, true)
            .then(company => {
                if (!company) {
                    // Through the getter: after a user switch the cache is empty, so nothing of the previous user
                    return fresh ? company : this.cache.get(id)
                }

                if (generation === this.cacheGeneration) {
                    this.fetchedAt.set(id, Date.now())
                }

                return company
            })
            .finally(() => {
                if (this.requests.get(id) === request) {
                    this.requests.delete(id)
                }
            })

        this.requests.set(id, request)

        return request
    }

    async logout(): Promise<void> {
        this.clearCache()

        return super.logout()
    }

    async getCurrentCompany(): Promise<Company> {
        this.checkCacheOwner()

        if (!this.currentCompany && this.isAuthenticated) {
            await this.loadCurrentCompany(false)
        }

        return this.currentCompany
    }

    /**
     * Fetches the signed-in user's company and makes it the current company, unless another user signed in meanwhile:
     * the previous user's company must not become theirs
     */
    protected async loadCurrentCompany(fresh: boolean): Promise<void> {
        const owner = this.cacheOwnerKey
        const company = await this.get(this.decodedAccessToken.companyId, fresh)

        // A reload that fails keeps the company we have
        if (owner !== null && owner === this.cacheOwnerKey && (company || !this.currentCompany)) {
            this.currentCompany = company
            this.reactiveCurrentCompany.value = company
        }
    }

    /**
     * The branches the current company can refer to through its active outgoing referral nodes, each with its company.
     * The companies are fetched in parallel; one that can't be fetched has no branches to offer.
     */
    async getOutgoingReferralBranches(): Promise<Branch[]> {
        const nodes = (await this.getOutgoingReferrals()) || []
        const companies = await Promise.all(nodes
            .filter(node => node.active !== false)
            .map(node => this.get(node.target.id)))

        const branches = new Map<string, Branch>()
        companies.forEach(company => company?.getAvailableBranches().forEach(branch => {
            branches.set(branch.id, { ...branch, company } as Branch)
        }))

        return Array.from(branches.values())
    }

    /**
     * After a link (referral node, pipeline connection) was created, changed or removed: what keyvue-api shows of
     * the other companies may have changed, so they are fetched again when they are needed. A request on its way
     * (from before the change) is dropped and doesn't fill the cache.
     */
    forgetRelatedCompanies(): void {
        const currentCompanyId = this.currentCompanyId

        Array.from(this.cache.keys())
            .filter(id => id !== currentCompanyId)
            .forEach(id => this.cache.delete(id))

        this.fetchedAt.clear()
        this.requests.clear()
        this.cacheGeneration++
    }

    /** Everything this client remembers, also the current company */
    clearCache(): void {
        super.clearCache()
        this.fetchedAt.clear()
        this.requests.clear()
        this.currentCompany = null
        this.reactiveCurrentCompany.value = null
    }

    protected isUpToDate(id: string, company: Company): boolean {
        if (id === this.currentCompanyId) {
            return true
        }

        const fetchedAt = this.fetchedAt.get(id)
        const isPartnerCard = company.branches.length === 0 && company.users.length === 0
        const lifetime = isPartnerCard ? PARTNER_CARD_LIFETIME : RELATED_COMPANY_LIFETIME

        return fetchedAt !== undefined && Date.now() - fetchedAt < lifetime
    }

    protected get currentCompanyId(): string|null {
        const token = this.isAuthenticated ? this.decodedAccessToken : null

        return token ? token.companyId : null
    }

    async reloadCurrentCompany(): Promise<Company> {
        if (this.isAuthenticated) {
            await this.loadCurrentCompany(true)
        }

        return this.currentCompany
    }

    async getIncomingReferrals(): Promise<ReferralNode[]> {
        if (!this.decodedAccessToken) {
            return []
        }

        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${this.decodedAccessToken.companyId}/incoming_referrals`)
            return Promise.all(response.data['hydra:member'].map(async referral => ReferralNode.fromData(referral)))

        } catch (error) {
            this.handleError(error)
        }
    }

    async getOutgoingReferrals(): Promise<ReferralNode[]> {
        if (!this.decodedAccessToken) {
            return []
        }

        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${this.decodedAccessToken.companyId}/outgoing_referrals`)
            return Promise.all(response.data['hydra:member'].map(async referral => ReferralNode.fromData(referral)))

        } catch (error) {
            this.handleError(error)
        }
    }

    async getIncomingConnections(cancelToken: CancelToken = null): Promise<PipelineConnection[]> {
        if (!this.decodedAccessToken) {
            return []
        }

        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${this.decodedAccessToken.companyId}/incoming_connections`, { cancelToken })
            return Promise.all(response.data['hydra:member'].map(async referral => PipelineConnection.fromData(referral)))

        } catch (error) {
            this.handleError(error)
        }
    }

    async getOutgoingConnections(cancelToken: CancelToken = null): Promise<PipelineConnection[]> {
        if (!this.decodedAccessToken) {
            return []
        }

        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${this.decodedAccessToken.companyId}/outgoing_connections`, { cancelToken })
            return Promise.all(response.data['hydra:member'].map(async referral => PipelineConnection.fromData(referral)))

        } catch (error) {
            this.handleError(error)
        }
    }

    async updateWidgetSettings(id: string, data: Company): Promise<Company> {
        try {
            const response = await this.client.put(`${this.basePath}/${this.collectionName}/${id}/widget_settings`, {
                widgetEnabled: data.widgetEnabled,
                widgetType: data.widgetType,
                widgetFlowTypes: data.widgetFlowTypes,
                widgetGoals: data.widgetGoals,
                widgetPopupType: data.widgetPopupType,
                widgetPopupFlowType: data.widgetPopupFlowType,
                widgetPopupPosition: data.widgetPopupPosition,
                widgetPopupAnimation: data.widgetPopupAnimation,
                widgetPopupBackgroundColor: data.widgetPopupBackgroundColor,
                widgetPopupTextColor: data.widgetPopupTextColor,
                widgetPopupButtonColor: data.widgetPopupButtonColor,
                widgetPopupButtonTextColor: data.widgetPopupButtonTextColor,
                widgetPopupFont: data.widgetPopupFont,
                widgetPopupContents: data.widgetPopupContents,
                widgetAvatarBackgroundColor: data.widgetAvatarBackgroundColor,
                widgetAvatarTextColor: data.widgetAvatarTextColor,
                widgetInformationTopics: data.widgetInformationTopics,
                widgetChatTextBackground: data.widgetChatTextBackground,
                widgetChatText: data.widgetChatText,
                widgetChatUserText: data.widgetChatUserText,
                widgetChatUserTextBorder: data.widgetChatUserTextBorder,
                widgetChatInputBorder: data.widgetChatInputBorder,
                widgetChatInputText: data.widgetChatInputText,
                widgetChatInputButtonActive: data.widgetChatInputButtonActive,
                widgetChatInputButtonInactive: data.widgetChatInputButtonInactive,
                widgetChatInputButton: data.widgetChatInputButton,
                widgetChatInputButtonText: data.widgetChatInputButtonText,
                widgetChatInputMultiText: data.widgetChatInputMultiText,
                widgetChatInputMultiBorder: data.widgetChatInputMultiBorder,
                widgetChatInputMultiSelectedText: data.widgetChatInputMultiSelectedText,
                widgetChatInputMultiSelectedBorder: data.widgetChatInputMultiSelectedBorder,
                widgetChatInputButtonActiveText: data.widgetChatInputButtonActiveText,
                widgetChatInputButtonInactiveText: data.widgetChatInputButtonInactiveText,
                widgetAvatarCircle: data.widgetAvatarCircle,
                widgetAvatarStyle: data.widgetAvatarStyle,
                widgetPopupOpenTime: data.widgetPopupOpenTime,
            })
            return data
        } catch (error) {
            this.handleError(error)
        }
    }

    async updateActiveCampaignCredentials(id: string, data: ActiveCampaignCredentials): Promise<void> {
        await this.client.put(`${this.basePath}/${this.collectionName}/${id}/active_campaign_credentials`, {
            includeInternalContacts: data.includeInternalContacts,
            includeExternalContacts: data.includeExternalContacts,
            includeRealworksRelaties: data.includeRealworksRelaties,
            syncedRealworksKenmerken: data.syncedRealworksKenmerken,
            excludedRealworksKenmerken: data.excludedRealworksKenmerken,
            syncedRealworksRelatietypes: data.syncedRealworksRelatietypes,
            syncedRealworksStatus: data.syncedRealworksStatus,
            updatedFrom: data.updatedFrom,
            updatedUntil: data.updatedUntil,
            apiUrl: data.apiUrl,
            apiKey: data.apiKey,
            eventTrackingAccountId: data.eventTrackingAccountId,
            eventTrackingKey: data.eventTrackingKey,
            defaultTeams: data.defaultTeams,
            defaultStatus: data.defaultStatus,
            emmaDefaults: data.emmaDefaults || {},
            syncedRealworksAgendatypes: data.syncedRealworksAgendatypes,
            syncedRealworksTaaktypes: data.syncedRealworksTaaktypes,
            enabled: data.enabled,
            user: data.updatedBy instanceof User ? data.updatedBy.resourceIdentifier : null,
        })
    }

    async updateMailchimpCredentials(id: string, data: MailchimpCredentials): Promise<void> {
        const response = await this.client.put(`${this.basePath}/${this.collectionName}/${id}/mailchimp_credentials`, {
            includeInternalContacts: data.includeInternalContacts,
            includeExternalContacts: data.includeExternalContacts,
            includeRealworksRelaties: data.includeRealworksRelaties,
            syncedRealworksKenmerken: data.syncedRealworksKenmerken,
            updatedFrom: data.updatedFrom,
            updatedUntil: data.updatedUntil,
            enabled: data.enabled,
            user: data.updatedBy instanceof User ? data.updatedBy.resourceIdentifier : null,
        })
    }

    async updateTypeformCredentials(id: string, data: TypeformCredentials): Promise<TypeformCredentials> {
        try {
            const response = await this.client.put(`${this.basePath}/${this.collectionName}/${id}/typeform_credentials`, {
                apiKey: data.apiKey,
                enabled: data.enabled,
                firstViewingAgendaTypes: data.firstViewingAgendaTypes,
                secondViewingAgendaTypes: data.secondViewingAgendaTypes,
                user: data.updatedBy instanceof User ? data.updatedBy.resourceIdentifier : null,
            })
            return data
        } catch (error) {
            this.handleError(error)
        }
    }

    async getOrderSettings(companyId: string): Promise<OrderSettings> {
        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${companyId}/order_settings`)
            return OrderSettings.fromData(response.data)

        } catch (error) {
            this.handleError(error)
        }
    }

    async updateOrderSettings(companyId: string, orderSettings: OrderSettings): Promise<OrderSettings> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${companyId}/order_settings`, orderSettings)
            return await this.getOrderSettings(companyId)

        } catch (error) {
            this.handleError(error)
        }
    }

    async getAutoCloseSettings(companyId: string): Promise<AutoCloseSettings> {
        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${companyId}/auto_close_settings`)
            return AutoCloseSettings.fromData(response.data)

        } catch (error) {
            this.handleError(error)
        }
    }

    async updateAutoCloseSettings(companyId: string, autoCloseSettings: AutoCloseSettings): Promise<AutoCloseSettings> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${companyId}/auto_close_settings`, autoCloseSettings)
            return await this.getAutoCloseSettings(companyId)

        } catch (error) {
            this.handleError(error)
        }
    }

    async updatePipelinesAllTab(company: Company): Promise<void> {
            await this.client.put(`${this.basePath}/${this.collectionName}`, {
            pipelinePhaseShowAllTab: company.pipelinePhaseShowAllTab,
        })
    }
}
